import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layers, Upload, ChevronRight, Loader2, Check, Sparkles, ArrowLeft, Camera, FileSearch, Save, Package, Target, FileText, Link as LinkIcon, Heart, ChevronLeft, CalendarIcon, X } from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { api } from '@/lib/api'
import { getAssetAspectRatioStyle, getAssetThumbnailUrl } from '@/lib/assets'
import { cn } from '@/lib/utils'
import { getDateFilters, TIME_RANGE_OPTIONS, type TimeRange } from '@/lib/date-filters'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toaster'
import { DirectionCard } from '@/components/variation/DirectionCard'
import { AspectRatioSelector } from '@/components/variation/AspectRatioSelector'
import type { AspectRatioOption } from '@/components/variation/AspectRatioSelector'
import type { Asset, Product, VariationDirection, VariationAnalyzeResponse, VariationGenerateResponse, SourceType } from '@/types'
import { embedRequirementId } from '@/lib/embedContext'

type Step = 'select-asset' | 'analyzing' | 'select-direction' | 'generating' | 'result'

const NANO_BANANA_2_ASPECT_RATIOS: AspectRatioOption[] = [
  { value: '1:1', label: '1:1 Square', description: '1:1' },
  { value: '21:9', label: '21:9 Cinematic', description: '21:9' },
  { value: '16:9', label: '16:9 Wide', description: '16:9' },
  { value: '5:4', label: '5:4 Wide', description: '5:4' },
  { value: '4:3', label: '4:3 Standard', description: '4:3' },
  { value: '3:2', label: '3:2 Photo', description: '3:2' },
  { value: '9:16', label: '9:16 Tall', description: '9:16' },
  { value: '3:4', label: '3:4 Portrait', description: '3:4' },
  { value: '2:3', label: '2:3 Portrait', description: '2:3' },
  { value: '4:5', label: '4:5 Feed', description: '4:5' },
]

const SOURCE_TYPE_CONFIG: Record<SourceType, { label: string; icon: typeof Camera }> = {
  screenshot: { label: 'Screenshot', icon: Camera },
  uploaded: { label: 'Uploaded', icon: Upload },
  generated: { label: 'Generated', icon: Sparkles },
  reference: { label: 'Reference', icon: FileSearch },
  feedback_reference: { label: 'FB Ref', icon: FileSearch },
  feedback_variant: { label: 'FB Variant', icon: FileSearch },
}

export function VariationGeneratePage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // State
  const [step, setStep] = useState<Step>('select-asset')
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [analysisResult, setAnalysisResult] = useState<VariationAnalyzeResponse | null>(null)
  const [selectedDirections, setSelectedDirections] = useState<VariationDirection[]>([])
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [variationDegree, setVariationDegree] = useState(50)
  const [generatedResults, setGeneratedResults] = useState<VariationGenerateResponse[]>([])
  const [creditsConsumed, setCreditsConsumed] = useState<number>(0)

  // Product info state (auto-loaded from linked product, or manual input)
  const [linkedProduct, setLinkedProduct] = useState<Product | null>(null)
  const [editedProductInfo, setEditedProductInfo] = useState({
    product_name: '',
    description: '',
    selling_points: '',
    target_market: '',
    target_audience: '',
  })
  const [productInfoDirty, setProductInfoDirty] = useState(false)
  const [productInfoLoading, setProductInfoLoading] = useState(false)

  // User preference (HIGH PRIORITY in AI analysis)
  const [userPreference, setUserPreference] = useState<string>('')

  // Filter state
  const [filterProductId, setFilterProductId] = useState<string>('')
  const [filterSourceType, setFilterSourceType] = useState<SourceType | ''>('')
  const [filterFavorited, setFilterFavorited] = useState<boolean | null>(null)
  const [timeRange, setTimeRange] = useState<TimeRange>('')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  // Pagination state
  const [offset, setOffset] = useState(0)
  const limit = 40

  // Computed
  const dateFilters = getDateFilters(timeRange, dateRange)
  const hasFilters = filterProductId || filterSourceType || filterFavorited !== null || timeRange

  const clearFilters = () => {
    setFilterProductId('')
    setFilterSourceType('')
    setFilterFavorited(null)
    setTimeRange('')
    setDateRange(undefined)
    setOffset(0)
  }

  // Fetch products for filter dropdown
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.getProducts(0, 100),
  })

  // Fetch user's assets with filters and pagination
  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['assets', 'for-variation', filterProductId, filterSourceType, filterFavorited, timeRange, dateRange?.from?.toISOString(), dateRange?.to?.toISOString(), offset, embedRequirementId],
    queryFn: () => api.getAssets({
      product_id: filterProductId || undefined,
      source_type: filterSourceType || undefined,
      is_favorited: filterFavorited ?? undefined,
      asset_type: 'image',
      created_after: dateFilters.created_after,
      created_before: dateFilters.created_before,
      offset,
      limit,
      include_original_urls: false,
      requirement_id: embedRequirementId ?? undefined,
    }),
  })

  // Auto-load product info when asset is selected
  useEffect(() => {
    if (selectedAsset?.product_id) {
      setProductInfoLoading(true)
      api.getProduct(selectedAsset.product_id)
        .then((product) => {
          setLinkedProduct(product)
          setEditedProductInfo({
            product_name: product.product_name || '',
            description: product.description || '',
            selling_points: product.selling_points || '',
            target_market: product.target_market || '',
            target_audience: product.target_audience || '',
          })
          setProductInfoDirty(false)
        })
        .catch(() => {
          setLinkedProduct(null)
          setEditedProductInfo({
            product_name: '',
            description: '',
            selling_points: '',
            target_market: '',
            target_audience: '',
          })
        })
        .finally(() => {
          setProductInfoLoading(false)
        })
    } else {
      // No linked product
      setLinkedProduct(null)
      setEditedProductInfo({
        product_name: '',
        description: '',
        selling_points: '',
        target_market: '',
        target_audience: '',
      })
      setProductInfoDirty(false)
    }
  }, [selectedAsset])

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: () => api.updateProduct(linkedProduct!.id, editedProductInfo),
    onSuccess: () => {
      setProductInfoDirty(false)
      toast({ title: 'Product information updated' })
    },
    onError: () => {
      toast({ title: 'Failed to update product', variant: 'destructive' })
    },
  })

  // Handle product info field change
  const handleProductInfoChange = (field: keyof typeof editedProductInfo, value: string) => {
    setEditedProductInfo((prev) => ({ ...prev, [field]: value }))
    setProductInfoDirty(true)
  }

  // Stage 1: Analyze mutation
  const analyzeMutation = useMutation({
    mutationFn: () => {
      // Build product_info from form (works for both linked and manual input)
      const hasProductInfo = editedProductInfo.product_name || editedProductInfo.description ||
        editedProductInfo.selling_points || editedProductInfo.target_market || editedProductInfo.target_audience

      const productInfoPayload = hasProductInfo ? {
        product_name: editedProductInfo.product_name,
        description: editedProductInfo.description,
        selling_points: editedProductInfo.selling_points,
        target_market: editedProductInfo.target_market,
        target_audience: editedProductInfo.target_audience,
      } : undefined

      return api.analyzeForVariations({
        source_asset_id: selectedAsset!.id,
        product_id: linkedProduct?.id || selectedAsset!.product_id || undefined,
        product_info: productInfoPayload,
        num_variations: 10,
        user_preference: userPreference || undefined,
      })
    },
    onMutate: () => {
      setStep('analyzing')
    },
    onSuccess: (result) => {
      setAnalysisResult(result)
      setStep('select-direction')
      toast({ title: 'Analysis complete! Select a variation direction.' })
    },
    onError: () => {
      setStep('select-asset')
      toast({ title: 'Analysis failed', variant: 'destructive' })
    },
  })

  // Stage 2: Generate mutation (batch - supports 1-10 directions)
  const generateMutation = useMutation({
    mutationFn: () =>
      api.generateVariationV2({
        analysis_id: analysisResult!.analysis_id,
        direction_ids: selectedDirections.map(d => d.id),
        aspect_ratio: aspectRatio,
        tool_id: 'gemini_image_flash',
        variation_degree: variationDegree,
        requirement_id: embedRequirementId ?? undefined,
      }),
    onMutate: () => {
      setStep('generating')
    },
    onSuccess: (result) => {
      setGeneratedResults(result.variations)
      setCreditsConsumed(result.credits_consumed)
      setStep('result')
      // Refresh subscription to show updated credits
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      // Refresh assets so newly generated images appear without manual reload
      queryClient.invalidateQueries({ queryKey: ['assets'] })
      const successCount = result.total_succeeded
      const failedCount = result.total_failed
      if (failedCount > 0) {
        toast({ title: `Generated ${successCount} variations (${failedCount} failed)` })
      } else {
        toast({ title: `Generated ${successCount} variation${successCount > 1 ? 's' : ''} successfully!` })
      }
      if (window.self !== window.top) {
        const imageUrls = result.variations.map((v) => v.asset_url).filter(Boolean) as string[]
        if (imageUrls.length > 0) {
          window.parent.postMessage({
            type: 'ASSETS_GENERATED',
            assets: imageUrls.map((url) => ({ url, media_type: 'image' })),
            requirement_id: embedRequirementId,
          }, '*')
        }
      }
    },
    onError: () => {
      setStep('select-direction')
      toast({ title: 'Generation failed', variant: 'destructive' })
    },
  })

  // Reset to start
  const handleReset = () => {
    setStep('select-asset')
    setSelectedAsset(null)
    setLinkedProduct(null)
    setEditedProductInfo({
      product_name: '',
      description: '',
      selling_points: '',
      target_market: '',
      target_audience: '',
    })
    setProductInfoDirty(false)
    setUserPreference('')
    setAnalysisResult(null)
    setSelectedDirections([])
    setVariationDegree(50)
    setGeneratedResults([])
    setCreditsConsumed(0)
  }

  // Assets are already filtered by API (asset_type: 'image')
  const imageAssets = assets?.items || []

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-0 text-sm">
        <StepIndicator
          step={1}
          label="Select Image"
          active={step === 'select-asset'}
          completed={step !== 'select-asset'}
        />
        <div className={cn('flex-1 h-0.5 mx-3 rounded-full max-w-16', step !== 'select-asset' ? 'bg-green-500' : 'bg-gray-200')} />
        <StepIndicator
          step={2}
          label="Choose Direction"
          active={step === 'analyzing' || step === 'select-direction'}
          completed={step === 'generating' || step === 'result'}
        />
        <div className={cn('flex-1 h-0.5 mx-3 rounded-full max-w-16', (step === 'generating' || step === 'result') ? 'bg-green-500' : 'bg-gray-200')} />
        <StepIndicator
          step={3}
          label="Generate"
          active={step === 'generating' || step === 'result'}
          completed={step === 'result'}
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm p-6">
        {/* Step 1: Select Asset */}
        {step === 'select-asset' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">1. Select Source Image</h2>

              {/* Filter Bar */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg space-y-3">
                {/* Row 1: Product, Source */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  {/* Product Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Product</span>
                    <select
                      value={filterProductId}
                      onChange={(e) => {
                        setFilterProductId(e.target.value)
                        setOffset(0)
                      }}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    >
                      <option value="">All Products</option>
                      {products?.items.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.product_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="h-6 w-px bg-gray-200" />

                  {/* Source Type Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Source</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setFilterSourceType('')
                          setOffset(0)
                        }}
                        className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                          filterSourceType === '' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      {(Object.keys(SOURCE_TYPE_CONFIG) as SourceType[]).map((type) => {
                        const config = SOURCE_TYPE_CONFIG[type]
                        const Icon = config.icon
                        return (
                          <button
                            key={type}
                            onClick={() => {
                              setFilterSourceType(type)
                              setOffset(0)
                            }}
                            className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                              filterSourceType === type
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <Icon className="h-3 w-3" />
                            {config.label}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                </div>

                {/* Row 2: Favorites, Time Range, Clear */}
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  {/* Favorites Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Collection</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setFilterFavorited(null)
                          setOffset(0)
                        }}
                        className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                          filterFavorited === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setFilterFavorited(true)
                          setOffset(0)
                        }}
                        className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                          filterFavorited === true
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Heart className="h-3 w-3" />
                        Favorites
                      </button>
                    </div>
                  </div>

                  <div className="h-6 w-px bg-gray-200" />

                  {/* Time Range Filter */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Time</span>
                    <div className="flex items-center gap-1">
                      {TIME_RANGE_OPTIONS.map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => {
                            setTimeRange(value)
                            if (value !== 'custom') setDateRange(undefined)
                            setOffset(0)
                          }}
                          className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                            timeRange === value
                              ? 'bg-gray-900 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            onClick={() => {
                              setTimeRange('custom')
                              setOffset(0)
                            }}
                            className={`px-2.5 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                              timeRange === 'custom'
                                ? 'bg-gray-900 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <CalendarIcon className="h-3 w-3" />
                            {timeRange === 'custom' && dateRange?.from ? (
                              dateRange.to ? (
                                <>{format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d')}</>
                              ) : (
                                format(dateRange.from, 'MMM d')
                              )
                            ) : (
                              'Custom'
                            )}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="range"
                            defaultMonth={dateRange?.from}
                            selected={dateRange}
                            onSelect={(range) => {
                              setDateRange(range)
                              setTimeRange('custom')
                              setOffset(0)
                            }}
                            numberOfMonths={1}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Clear Filters + Result Count */}
                  <div className="flex items-center gap-3 ml-auto">
                    {hasFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                      >
                        <X className="h-3 w-3" />
                        Clear all
                      </button>
                    )}
                    <span className="text-xs text-gray-500">
                      {assetsLoading ? 'Loading...' : `${assets?.total || 0} images`}
                    </span>
                  </div>
                </div>
              </div>

              {imageAssets.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No images found. {hasFilters ? 'Try changing filters.' : 'Upload an image first.'}</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {imageAssets.map((asset) => (
                      <div
                        key={asset.id}
                        onClick={() => setSelectedAsset(asset)}
                        className={cn(
                          'cursor-pointer rounded-lg overflow-hidden border-2 transition-all relative group',
                          selectedAsset?.id === asset.id
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : 'border-transparent hover:border-gray-300'
                        )}
                      >
                        {getAssetThumbnailUrl(asset) ? (
                          <img
                            src={getAssetThumbnailUrl(asset)!}
                            alt=""
                            className={`w-full object-contain bg-gray-50 ${
                              getAssetAspectRatioStyle(asset) ? '' : 'aspect-square'
                            }`}
                            style={getAssetAspectRatioStyle(asset)}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                            <Upload className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        {/* Favorite badge */}
                        {asset.is_favorited && (
                          <div className="absolute top-1 right-1 p-1 rounded-full bg-white/80">
                            <Heart className="h-3 w-3 fill-red-500 text-red-500" />
                          </div>
                        )}
                        {/* Source type badge */}
                        <div className="absolute bottom-1 left-1 px-1.5 py-0.5 text-xs rounded bg-black/60 text-white">
                          {asset.source_type === 'screenshot' && <Camera className="h-3 w-3 inline mr-1" />}
                          {asset.source_type === 'generated' && <Sparkles className="h-3 w-3 inline mr-1" />}
                          {asset.source_type === 'uploaded' && <Upload className="h-3 w-3 inline mr-1" />}
                          {asset.source_type === 'reference' && <FileSearch className="h-3 w-3 inline mr-1" />}
                          {asset.source_type}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {assets && assets.total > limit && (
                    <div className="flex items-center justify-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - limit))}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <span className="text-sm text-gray-500">
                        Page {Math.floor(offset / limit) + 1} of {Math.ceil(assets.total / limit)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={offset + limit >= assets.total}
                        onClick={() => setOffset(offset + limit)}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Product Information Section */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-gray-500" />
                  <h3 className="text-base font-semibold">Product Information</h3>
                </div>
                {linkedProduct && (
                  <div className="flex items-center gap-2">
                    {productInfoDirty && (
                      <span className="text-sm text-amber-600">Unsaved changes</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateProductMutation.mutate()}
                      disabled={!productInfoDirty || updateProductMutation.isPending}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {updateProductMutation.isPending ? 'Saving...' : 'Update'}
                    </Button>
                  </div>
                )}
              </div>

              {productInfoLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading product info...</span>
                </div>
              ) : linkedProduct ? (
                /* Linked product: show structured editable form */
                <div className="space-y-6">
                  {/* Header with link badge */}
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                    <LinkIcon className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-blue-700">
                      Linked to: <span className="font-medium">{linkedProduct.product_name}</span>
                    </span>
                  </div>

                  {/* Basic Info Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Package className="h-4 w-4 text-gray-400" />
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={editedProductInfo.product_name}
                        onChange={(e) => handleProductInfoChange('product_name', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Target className="h-4 w-4 text-gray-400" />
                        Target Market
                      </label>
                      <input
                        type="text"
                        value={editedProductInfo.target_market}
                        onChange={(e) => handleProductInfoChange('target_market', e.target.value)}
                        placeholder="e.g., North America, Europe"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <FileText className="h-4 w-4 text-gray-400" />
                      Description
                    </label>
                    <textarea
                      value={editedProductInfo.description}
                      onChange={(e) => handleProductInfoChange('description', e.target.value)}
                      placeholder="Describe your product..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>

                  {/* Selling Points */}
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Sparkles className="h-4 w-4 text-gray-400" />
                      Selling Points
                    </label>
                    <textarea
                      value={editedProductInfo.selling_points}
                      onChange={(e) => handleProductInfoChange('selling_points', e.target.value)}
                      placeholder="Key features and benefits..."
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>

                  {/* Target Audience */}
                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Target className="h-4 w-4 text-gray-400" />
                      Target Audience
                    </label>
                    <input
                      type="text"
                      value={editedProductInfo.target_audience}
                      onChange={(e) => handleProductInfoChange('target_audience', e.target.value)}
                      placeholder="e.g., Young professionals, 25-35"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                  </div>
                </div>
              ) : (
                /* No linked product: show structured form for manual input */
                <div className="space-y-6">
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                    <Package className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-700">
                      No linked product. Provide details to help generate better variations.
                    </span>
                  </div>

                  {/* Manual Input Form - Structured */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Package className="h-4 w-4 text-gray-400" />
                        Product Name
                      </label>
                      <input
                        type="text"
                        value={editedProductInfo.product_name}
                        onChange={(e) => handleProductInfoChange('product_name', e.target.value)}
                        placeholder="Enter product name"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                        <Target className="h-4 w-4 text-gray-400" />
                        Target Market
                      </label>
                      <input
                        type="text"
                        value={editedProductInfo.target_market}
                        onChange={(e) => handleProductInfoChange('target_market', e.target.value)}
                        placeholder="e.g., North America, Europe"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <FileText className="h-4 w-4 text-gray-400" />
                      Description
                    </label>
                    <textarea
                      value={editedProductInfo.description}
                      onChange={(e) => handleProductInfoChange('description', e.target.value)}
                      placeholder="Describe your product..."
                      rows={3}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                      <Sparkles className="h-4 w-4 text-gray-400" />
                      Selling Points
                    </label>
                    <textarea
                      value={editedProductInfo.selling_points}
                      onChange={(e) => handleProductInfoChange('selling_points', e.target.value)}
                      placeholder="Key features and benefits..."
                      rows={2}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* User Preference (HIGH PRIORITY) */}
            <div className="border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-4 w-4 text-purple-600" />
                <h3 className="font-medium text-purple-800">Your Preferences (Optional)</h3>
              </div>
              <p className="text-sm text-purple-600 mb-3">
                Describe your style preferences - these will be given <strong>high priority</strong> in the AI analysis
              </p>
              <textarea
                placeholder="e.g., 'I prefer bright colors and modern minimalist style' or 'Focus on lifestyle scenes with warm lighting'"
                value={userPreference}
                onChange={(e) => setUserPreference(e.target.value)}
                className="w-full border border-purple-200 rounded-md p-3 h-20 resize-none bg-white focus:ring-2 focus:ring-purple-300 focus:border-purple-300"
              />
            </div>

            <Button
              className="w-full"
              size="lg"
              onClick={() => analyzeMutation.mutate()}
              disabled={!selectedAsset || analyzeMutation.isPending}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Analyze Image
            </Button>
          </div>
        )}

        {/* Analyzing State */}
        {step === 'analyzing' && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative mb-4">
              <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-ping" />
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 relative" />
            </div>
            <h3 className="text-lg font-medium">Analyzing your image...</h3>
            <p className="text-gray-500 mt-2">
              Our AI is examining composition, style, colors, and more
            </p>
          </div>
        )}

        {/* Step 2: Select Direction */}
        {step === 'select-direction' && analysisResult && (
          <div className="space-y-6">
            {/* Source Image Preview and Analysis */}
            <div className="flex gap-6">
              <div className="w-48 shrink-0">
                {analysisResult.source_asset_url && (
                  <img
                    src={analysisResult.source_asset_url}
                    alt="Source"
                    className="w-full rounded-lg shadow"
                  />
                )}
                <p className="text-xs text-gray-500 mt-2 text-center">Source Image</p>
              </div>

              {/* Analysis Summary */}
              <div className="flex-1">
                <h3 className="font-medium mb-3">Image Analysis</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {analysisResult.analysis.style && (
                    <div>
                      <span className="text-gray-500">Style:</span>{' '}
                      <span className="text-gray-900">{analysisResult.analysis.style}</span>
                    </div>
                  )}
                  {analysisResult.analysis.mood && (
                    <div>
                      <span className="text-gray-500">Mood:</span>{' '}
                      <span className="text-gray-900">{analysisResult.analysis.mood}</span>
                    </div>
                  )}
                  {analysisResult.analysis.color_palette.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-gray-500">Colors:</span>
                      <div className="flex gap-1 mt-1">
                        {analysisResult.analysis.color_palette.slice(0, 6).map((color, i) => (
                          <div
                            key={i}
                            className="w-6 h-6 rounded border border-gray-200"
                            style={{ backgroundColor: color }}
                            title={color}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Variation Directions - Multi-select */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">2. Choose Variation Directions</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (selectedDirections.length === analysisResult.directions.length) {
                      setSelectedDirections([])
                    } else {
                      setSelectedDirections([...analysisResult.directions].slice(0, 10))
                    }
                  }}
                >
                  {selectedDirections.length === analysisResult.directions.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                Select 1-10 directions to generate. Each direction costs 1 credit.
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {analysisResult.directions.map((direction) => (
                  <DirectionCard
                    key={direction.id}
                    direction={direction}
                    isSelected={selectedDirections.some(d => d.id === direction.id)}
                    onSelect={() => {
                      setSelectedDirections(prev => {
                        const isAlreadySelected = prev.some(d => d.id === direction.id)
                        if (isAlreadySelected) {
                          return prev.filter(d => d.id !== direction.id)
                        } else if (prev.length < 10) {
                          return [...prev, direction]
                        }
                        return prev // Max 10 selections
                      })
                    }}
                    multiSelect
                  />
                ))}
              </div>
              {selectedDirections.length >= 10 && (
                <p className="text-sm text-amber-600 mt-2">
                  Maximum 10 directions can be selected at once.
                </p>
              )}
            </div>

            {/* Aspect Ratio */}
            {selectedDirections.length > 0 && (
              <div>
                <h3 className="font-medium mb-3">Select Aspect Ratio</h3>
                <AspectRatioSelector
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  ratios={NANO_BANANA_2_ASPECT_RATIOS}
                />
              </div>
            )}

            {/* Variation Degree */}
            {selectedDirections.length > 0 && (
              <div>
                <h3 className="font-medium mb-3">Variation Degree</h3>
                <div className="flex items-center gap-3 max-w-xs">
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={5}
                    value={variationDegree}
                    onChange={(e) => setVariationDegree(Number(e.target.value))}
                    className="w-32 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                  />
                  <span className="text-sm font-medium whitespace-nowrap">
                    {variationDegree}% · {variationDegree <= 35 ? 'Subtle' : variationDegree <= 65 ? 'Moderate' : 'Bold'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {variationDegree <= 35
                    ? 'Very close to original, only minor differences'
                    : variationDegree <= 65
                      ? 'Noticeable changes with moderate creative freedom'
                      : 'Dramatic changes for a completely fresh look'}
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <Button variant="outline" onClick={handleReset}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Start Over
              </Button>
              <Button
                className="flex-1"
                size="lg"
                onClick={() => generateMutation.mutate()}
                disabled={selectedDirections.length === 0 || generateMutation.isPending}
              >
                <Layers className="h-4 w-4 mr-2" />
                Generate {selectedDirections.length} Variation{selectedDirections.length !== 1 ? 's' : ''} ({selectedDirections.length} Credit{selectedDirections.length !== 1 ? 's' : ''})
              </Button>
            </div>
          </div>
        )}

        {/* Generating State */}
        {step === 'generating' && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="relative mb-4">
              <div className="absolute inset-0 rounded-full bg-blue-400/20 animate-ping" />
              <Loader2 className="h-12 w-12 animate-spin text-blue-500 relative" />
            </div>
            <h3 className="text-lg font-medium">Generating {selectedDirections.length} variation{selectedDirections.length !== 1 ? 's' : ''}...</h3>
            <p className="text-gray-500 mt-2">
              {selectedDirections.length === 1
                ? `Applying "${selectedDirections[0].name}" to your image`
                : `Applying ${selectedDirections.length} directions in parallel`}
            </p>
          </div>
        )}

        {/* Result - Original on top, variations in grid below */}
        {step === 'result' && generatedResults.length > 0 && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold">
                {generatedResults.length} Variation{generatedResults.length !== 1 ? 's' : ''} Generated!
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {creditsConsumed} credit{creditsConsumed !== 1 ? 's' : ''} consumed
              </p>
            </div>

            {/* Original Image - Full width on top */}
            <div className="text-center">
              {analysisResult?.source_asset_url && (
                <img
                  src={analysisResult.source_asset_url}
                  alt="Original"
                  className="max-w-md mx-auto rounded-lg shadow"
                />
              )}
              <p className="text-sm text-gray-500 mt-2">Original</p>
            </div>

            {/* Generated Variations - Grid below */}
            <div>
              <h3 className="font-medium mb-3">Generated Variations</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {generatedResults.map((result) => (
                  <div key={result.variation_id} className="rounded-lg border overflow-hidden">
                    {result.asset_url ? (
                      <img
                        src={result.asset_url}
                        alt={result.direction_info.name}
                        className="w-full aspect-square object-contain bg-gray-50"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 flex items-center justify-center">
                        <p className="text-gray-500 text-sm">Processing...</p>
                      </div>
                    )}
                    <div className="p-2 bg-gray-50">
                      <p className="text-sm font-medium truncate">{result.direction_info.name}</p>
                      <p className="text-xs text-gray-500 truncate">{result.direction_info.dimension}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={handleReset}>
                Create Another
              </Button>
              <Button onClick={() => navigate('/tasks')}>View All Tasks</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Helper component for step indicator
function StepIndicator({
  step,
  label,
  active,
  completed,
}: {
  step: number
  label: string
  active: boolean
  completed: boolean
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2',
        active && 'text-blue-600 font-medium',
        completed && 'text-green-600',
        !active && !completed && 'text-gray-400'
      )}
    >
      <div
        className={cn(
          'w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all duration-200',
          active && 'bg-blue-600 text-white ring-4 ring-blue-100',
          completed && 'bg-green-600 text-white',
          !active && !completed && 'bg-gray-200 text-gray-600'
        )}
      >
        {completed ? <Check className="h-3 w-3" /> : step}
      </div>
      <span>{label}</span>
    </div>
  )
}
